import { useAppDispatch } from 'app/hooks'
import { CountType, InsightViewOption, Interest, MetricTimeUnit, Profession } from 'enums'
import { getWidgetForDashboard } from 'features/widget/asyncThunks'
import { useDashboardData, useDashboardWidgetSetting } from 'hooks/useDashboardState'
import { useEffect } from 'react'
import { Dashboard } from 'schemas/DashboardSchema'
import { createInsightCount, createMetric } from 'utils/runSearchUtil'
import { toFilter } from 'utils/widgetUtil'
import { getMeasureByCountTypeAndAnalysisType } from 'utils/measureUtil'
import { getMetricSeries } from 'utils/measureTypeUtil'
import { getInsightCategory } from 'utils/viewOptionUtil'
import { WidgetId } from 'features/types'
import useRefreshTimestamp from 'hooks/useRefreshTimestamp'

import { DemographicWidgetsType } from '../../types'

const createSearches = (
  widgetSetting: DemographicWidgetsType,
  dashboardData: Dashboard,
  isAudienceQuery: boolean
) => {
  const {
    selectedTopic,
    precision,
    viewOption,
    countType,
    analysisType,
    comparedTopicInfos,
    topN // * note: in Analyze if viewOption === InsightViewOption.BIOTEXT need to pass 50 as default topN
  } = widgetSetting

  const filter = toFilter(widgetSetting)
  const topicIds = [selectedTopic.topicId!]
  const comparedIds = [comparedTopicInfos![0].topicId!]
  const { id: dashboardId, name } = dashboardData
  const measure = getMeasureByCountTypeAndAnalysisType(countType!, analysisType!)
  const insightCountOption = {
    showPrimaryObject: false,
    isAuthorBased: countType === CountType.AUTHORS,
    exactCount: topN,
    comparedTopicIds: comparedIds,
    emojiCategory: undefined
  }
  const metricOption = {
    isAudienceQuery
  }

  const searches = []

  const baseResultSearch = createInsightCount(
    dashboardId!,
    name!,
    precision!,
    topicIds,
    filter,
    measure!,
    getInsightCategory(viewOption!, analysisType!),
    insightCountOption
  )

  searches.push(baseResultSearch)

  let needMetricSearch = false

  if (viewOption === InsightViewOption.INTERESTS) {
    filter.includedInterests ??= []
    filter.includedInterests.push(Object.values(Interest))
    needMetricSearch = true
  } else if (viewOption === InsightViewOption.PROFESSIONS) {
    filter.includedProfessions ??= []
    filter.includedProfessions.push(Object.values(Profession))
    needMetricSearch = true
  }

  if (needMetricSearch) {
    const totalPostSearch = createMetric(
      dashboardId!,
      name!,
      precision!,
      topicIds,
      filter,
      getMetricSeries(measure!),
      [MetricTimeUnit.ALL],
      metricOption
    )
    searches.push(totalPostSearch)
  }

  return searches
}

const RunSearchEffect = ({
  widgetId,
  children,
  isAudienceQuery = false
}: {
  widgetId: WidgetId
  children: React.ReactElement
  isAudienceQuery?: boolean
}) => {
  const widgetSetting = useDashboardWidgetSetting<DemographicWidgetsType>(widgetId)
  const dashboardData = useDashboardData()
  const dispatch = useAppDispatch()
  const refreshTimestamp = useRefreshTimestamp(widgetId)

  useEffect(() => {
    const searches = createSearches(widgetSetting, dashboardData!, isAudienceQuery)

    dispatch(
      getWidgetForDashboard({
        searches,
        widgetId: widgetSetting.widgetId!,
        timestamp: refreshTimestamp
      })
    )
  }, [widgetSetting, dashboardData, dispatch, isAudienceQuery, refreshTimestamp])

  return children
}

export default RunSearchEffect
